import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { MonogramPageJson } from '@shared/websiteTemplates/websiteTemplates.types';
import { applyMonogramPatterns, buildMonogramPatternContext } from '@shared/websiteTemplates/monogramPattern.utils';

export const applyColorsToMonogram = (json: PageJson, background?: string, fontColor?: string): PageJson => {
  return {
    fill: background || json.fill,
    layers: json.layers.map(layer => {
      if (layer.type === 'text') {
        return {
          ...layer,
          textData: {
            ...layer.textData,
            fill: fontColor || layer.textData.fill,
          },
        };
      }

      return layer;
    }),
  };
};

/**
 * Personalizes a raw monogram JSON container by resolving each layer's authored `pattern`
 * (see monogramPattern.utils) from the couple's names and event date. Returns the content unchanged
 * when there is no front; layers without a pattern (separators, or content not sourced from the
 * inline templates) are left untouched.
 *
 * Initials are only substituted when both partner names are present — a single real initial beside a
 * template placeholder produces a mixed monogram. The event date is applied independently.
 */
export const personalizeMonogramContent = (
  content: Record<string, unknown>,
  ownerFirstName: string | null | undefined,
  fianceeFirstName: string | null | undefined,
  eventDateMs: number | null | undefined,
): Record<string, unknown> => {
  const card = content?.card as Record<string, unknown> | undefined;
  const front = card?.front as MonogramPageJson | undefined;
  if (!front) return content;

  const personalizedFront = applyMonogramPatterns(front, buildMonogramPatternContext(ownerFirstName, fianceeFirstName, eventDateMs));

  return { ...content, card: { ...card, front: personalizedFront } };
};
